import { Filter, ValidationRules } from '../reportingTypes';
import { parseToday } from './formatting';

/**
 * Validate a single filter value
 */
export const validateFilter = (
  filter: Filter,
  value: unknown,
  allValues: Record<string, unknown>,
  allFilters?: Filter[]
): string | null => {
  // Check required
  if (filter.required) {
    if (value === null || value === undefined || value === '') {
      return `${filter.name} is required`;
    }
    if (Array.isArray(value) && value.length === 0) {
      return `${filter.name} is required`;
    }
  }

  // Skip validation if value is empty and not required
  if (!value || (Array.isArray(value) && value.length === 0)) {
    return null;
  }

  const validation = filter.validation;
  if (!validation) {
    return null;
  }

  // Date validation
  if (filter.type === 'date') {
    return validateDateFilter(
      filter.name,
      String(value),
      validation,
      allValues,
      allFilters
    );
  }

  // Text validation
  if (filter.type === 'text') {
    return validateTextFilter(filter.name, String(value), validation);
  }

  // Number validation
  if (filter.type === 'number') {
    return validateNumberFilter(filter.name, Number(value), validation);
  }

  // Multi-select validation
  if (filter.type === 'multiSelect') {
    const arrValue = Array.isArray(value) ? value : [];
    return validateMultiSelectFilter(filter.name, arrValue, validation);
  }

  return null;
};

/**
 * Format date from YYYY-MM-DD to DD/MM/YYYY
 */
const formatDateForDisplay = (dateStr: string): string => {
  try {
    const [year, month, day] = dateStr.split('-');
    if (year && month && day) {
      return `${day}/${month}/${year}`;
    }
    return dateStr;
  } catch {
    return dateStr;
  }
};

/**
 * Validate date filter
 */
const validateDateFilter = (
  name: string,
  value: string,
  validation: ValidationRules,
  allValues: Record<string, unknown>,
  allFilters?: Filter[]
): string | null => {
  const date = new Date(value);
  const today = parseToday();

  // Validate minDate
  if (validation.minDate) {
    const minDate = validation.minDate === 'today' ? today : validation.minDate;
    if (date < new Date(minDate)) {
      const formattedDate = formatDateForDisplay(minDate);
      return `${name} must be after ${formattedDate}`;
    }
  }

  // Validate maxDate
  if (validation.maxDate) {
    const maxDate = validation.maxDate === 'today' ? today : validation.maxDate;
    if (date > new Date(maxDate)) {
      const formattedDate = formatDateForDisplay(maxDate);
      return `${name} must be before or equal to ${formattedDate}`;
    }
  }

  // Validate relativeTo (e.g., startDate must be before endDate)
  if (validation.relativeTo) {
    const relatedValue = allValues[validation.relativeTo];
    if (relatedValue) {
      const relatedDate = new Date(String(relatedValue));
      // Assuming the current field should be before the related field
      if (date > relatedDate) {
        // Find the related filter's display name
        const relatedFilter = allFilters?.find(
          (f) => f.id === validation.relativeTo
        );
        const relatedName = relatedFilter?.name || validation.relativeTo;
        return `${name} must be before or equal to ${relatedName}`;
      }
    }
  }

  return null;
};

/**
 * Validate text filter
 */
const validateTextFilter = (
  name: string,
  value: string,
  validation: ValidationRules
): string | null => {
  // Validate minLength
  if (validation.minLength && value.length < validation.minLength) {
    return `${name} must be at least ${validation.minLength} characters`;
  }

  // Validate maxLength
  if (validation.maxLength && value.length > validation.maxLength) {
    return `${name} must be at most ${validation.maxLength} characters`;
  }

  // Validate pattern (regex)
  if (validation.pattern) {
    const regex = new RegExp(validation.pattern);
    if (!regex.test(value)) {
      return `${name} is invalid`;
    }
  }

  return null;
};

/**
 * Validate number filter
 */
const validateNumberFilter = (
  name: string,
  value: number,
  validation: ValidationRules
): string | null => {
  // Validate min
  if (validation.min !== undefined && value < validation.min) {
    return `${name} must be at least ${validation.min}`;
  }

  // Validate max
  if (validation.max !== undefined && value > validation.max) {
    return `${name} must be at most ${validation.max}`;
  }

  return null;
};

/**
 * Validate multi-select filter
 */
const validateMultiSelectFilter = (
  name: string,
  value: unknown[],
  validation: ValidationRules
): string | null => {
  // Validate minItems
  if (validation.minItems && value.length < validation.minItems) {
    return `${name} must have at least ${validation.minItems} items`;
  }

  // Validate maxItems
  if (validation.maxItems && value.length > validation.maxItems) {
    return `${name} must have at most ${validation.maxItems} items`;
  }

  return null;
};

/**
 * Validate all filters
 */
export const validateAllFilters = (
  filters: Filter[],
  values: Record<string, unknown>
): Record<string, string> => {
  const errors: Record<string, string> = {};

  filters.forEach((filter) => {
    // Skip hidden and system filters
    if (filter.hidden || filter.source === 'system') {
      return;
    }

    const error = validateFilter(filter, values[filter.id], values, filters);
    if (error) {
      errors[filter.id] = error;
    }
  });

  return errors;
};
